import GitHubIcon from "@material-ui/icons/GitHub";
import ArticleIcon from '@mui/icons-material/Article';
import Link from '@material-ui/core/Link';
import YoutubeEmbed from "../components/YoutubeEmbed";

import "../styles/ProjectDisplay.css";

import QuickNotesIcon from "../assets/icons/QuickNotes_Icon2.png";
import QuickNotesProject, { tech as quickNotesTech } from "../pages/QuickNotesProject";
import ShareInfoIcon from "../assets/graphics/ShareInfo_Graphic.png";
import ShareInfoProject, { tech as shareInfoTech } from "../pages/ShareInfoProject";
import PDFroggy_Icon from "../assets/icons/PDFroggy_Icon.png";
import LongShotIcon from "../assets/graphics/LongShot_Card.webp";
import LongShotProject, { tech as longShotTech } from "../pages/LongShotProject";
import SocialMediaIcon from "../assets/icons/SocialMedia_Icon.png";
import HealthSearchCard from "../assets/graphics/HealthSearch_Card.svg";
import HealthSearchProject, { tech as healthSearchTech, shots as healthSearchShots } from "../pages/HealthSearchProject";

// Card art for the Projects page: app icons and a pair of screenshots per featured project.
import QuickNotesAppIcon from "../assets/icons/QuickNotes_AppIcon.png";
import ShareInfoAppIcon from "../assets/icons/ShareInfo_AppIcon.png";
import qnSearchLight from "../assets/demo_screenshots/quicknotes/search-november-light.webp";
import qnSearchDark from "../assets/demo_screenshots/quicknotes/search-november-dark.webp";
import qnUpcomingLight from "../assets/demo_screenshots/quicknotes/upcoming-light.webp";
import qnUpcomingDark from "../assets/demo_screenshots/quicknotes/upcoming-dark.webp";
import siReportLight from "../assets/demo_screenshots/shareinfo/report-light.webp";
import siReportDark from "../assets/demo_screenshots/shareinfo/report-dark.webp";
import siTrendingLight from "../assets/demo_screenshots/shareinfo/trending-light.webp";
import siTrendingDark from "../assets/demo_screenshots/shareinfo/trending-dark.webp";
import lsAimShot from "../assets/demo_screenshots/longshot/aim.webp";
import lsTrickShot from "../assets/demo_screenshots/longshot/trick.webp";

import PDFroggy_DemoVideo from "../assets/videos/PDFroggy_DemoVideo.mp4";
import PDFroggy_Thumbnail from "../assets/videos/PDFroggy_Thumbnail.jpg";

export const ProjectList = [
  {
    id: "HealthSearch",
    name: "HealthSearch",
    type: "Plain-Language Health Search, Self-Hosted on a Raspberry Pi",
    image: HealthSearchCard,
    // Has its own page: a walkthrough with screenshots and how it's built. The live app is /healthsearch.
    page: HealthSearchProject,
    card: {
      featured: true,
      icon: HealthSearchCard,
      iconWide: true,
      eyebrow: "Web app · Self-hosted",
      pitch: "Ask a health question in plain words and get plain-language answers, nearby hospitals with real prices, and AI self-care suggestions from an LLM, checked by code, all served from a Raspberry Pi.",
      tech: healthSearchTech,
      shots: [
        { ...healthSearchShots.phoneResults, alt: "HealthSearch hospital card with a $449 knee MRI price and a map" },
        { ...healthSearchShots.medicines, alt: "HealthSearch prescription medicines with Cost Plus Drugs prices" },
      ],
      tint: "bg-[#dfe4f3] dark:bg-[#1b2547]",
      primary: { label: "Try it", to: "/healthsearch" },
    },
  },
  {
    id: "QuickNotes",
    name: "Quick Notes",
    type: "Notes Android App with On-Device AI Search and Time and Place Reminders",
    image: QuickNotesIcon,
    // Has its own page: a walkthrough of the app with screenshots.
    page: QuickNotesProject,
    card: {
      featured: true,
      icon: QuickNotesAppIcon,
      eyebrow: "Android app",
      pitch: "Finds any note by what you mean, with AI search that runs on the phone, and notes that remind you at a time, on a schedule, or at a place.",
      tech: quickNotesTech,
      shots: [
        { light: qnSearchLight, dark: qnSearchDark, alt: "Quick Notes search for november" },
        { light: qnUpcomingLight, dark: qnUpcomingDark, alt: "Quick Notes upcoming reminders" },
      ],
      tint: "bg-[#fde3e5] dark:bg-[#2a3346]",
      apk: { repo: "ElAyachie/Quick-Notes", file: "QuickNotes.apk" },
    },
  },
  {
    id: "ShareInfo",
    name: "Share Info",
    type: "Social Media Sentiment Android App for Stocks, Crypto and More",
    image: ShareInfoIcon,
    // Has its own page: a walkthrough of the app with screenshots.
    page: ShareInfoProject,
    card: {
      featured: true,
      icon: ShareInfoAppIcon,
      eyebrow: "Android app",
      pitch: "Reads StockTwits, Bluesky, Reddit, YouTube and the news, and turns the posts about any stock, coin or topic into one sentiment score.",
      tech: shareInfoTech,
      shots: [
        { light: siReportLight, dark: siReportDark, alt: "Share Info sentiment report for Apple" },
        { light: siTrendingLight, dark: siTrendingDark, alt: "Share Info trending topics" },
      ],
      tint: "bg-[#dff3e4] dark:bg-[#263a3a]",
      apk: { repo: "ElAyachie/Share-Info", file: "ShareInfo.apk" },
    },
  },
  {
    id: "LongShot",
    name: "Long Shot",
    type: "Unity Soccer Game, Playable in the Browser",
    image: LongShotIcon,
    // Has its own page: a walkthrough with screenshots, and the game itself to play.
    page: LongShotProject,
    card: {
      featured: true,
      icon: LongShotIcon,
      iconWide: true,
      eyebrow: "Unity game",
      pitch: "Score from your own half: aim past a line of defenders, bank shots off the boards and bend them around the keeper. Play it right in your browser.",
      tech: longShotTech,
      shots: [
        { light: lsAimShot, dark: lsAimShot, alt: "Long Shot aiming a shot past the defenders" },
        { light: lsTrickShot, dark: lsTrickShot, alt: "Long Shot scoring a super trick shot off the boards" },
      ],
      tint: "bg-[#e1efd9] dark:bg-[#2c3a33]",
      primary: { label: "Play in browser", to: "/projects/LongShot#play", play: true },
      apk: { repo: "ElAyachie/Long-Shot", file: "LongShot.apk" },
    },
  },
  {
    id: "PDFFroggy",
    name: "PDFroggy++",
    type: "Accessibility Focused PDF Reader Android Application",
    image: PDFroggy_Icon,
    technology: "Android Studio, Java, XML, PDFBox",
    card: {
      eyebrow: "Android app · Research",
      pitch: "A PDF reader that restructures documents for people with reading disabilities and non-native English speakers. Published in Frontiers in Computer Science, where the paper has nearly 8,000 views.",
      badge: "Peer-reviewed paper",
      tech: ["Java", "Android", "PDFBox"],
    },
    description:         
    <p>
      PDFroggy++ is a PDF reading application that allows users to change the structure of their PDF to allow for better readability and accessibility.<br/> 
      Users can edit the layout or theme and also create or download pre-defined JSON setting files to improve their reading experience.<br/><br/>
      The application targets those with reading disabilities, reading impairments, and non-native English speakers.<br/><br/>
      A peer-reviewed research paper on the app, <a href="https://www.frontiersin.org/journals/computer-science/articles/10.3389/fcomp.2021.628832/full" target="_blank" rel="noreferrer">Readability Enhancement for PDF Documents</a>, was published in Frontiers in Computer Science, where it has nearly 8,000 views and 1,600+ downloads. The application was developed with the help of Omar Abou Nassif Mourad and under the guidance of professor Chen-Hsiang Yu, Ph.D. at the Wentworth Institute of Technology. <br/>
    </p>,
    external:
    <div>
      <video width="100%" poster={PDFroggy_Thumbnail} controls>
        <source src={PDFroggy_DemoVideo} type="video/mp4"
        />
        Sorry, your browser doesn't support videos.
      </video>
      <div className="!w-full flex justify-center text-center items-center flex-col">
        <Link href="https://www.frontiersin.org/articles/10.3389/fcomp.2021.628832/full" target="_blank" rel="noreferrer">
          <ArticleIcon className="!text-6xl !fill-[#DC4441] dark:!fill-[#3f51b5]"/>
          <p className="text no-underline hover:no-underline text-black dark:text-white">Frontiers Media Journal</p>
        </Link>
      </div>
    </div>,
    copyright:
    <div className="pl-6">
      <p>
        Article: <a href="https://www.frontiersin.org/articles/10.3389/fcomp.2021.628832/full" target="_blank" rel="noreferrer"><u>Frontiers Article</u></a><br/><br/>
        
        Citation: Yu C-H, Shelton Z, Abou Nassif Mourad O and Oulal MA (2021) Readability Enhancement for PDF Documents. Front. Comput. Sci.<br/> 3:628832. doi: 10.3389/fcomp.2021.628832<br/><br/>
        
        Received: 13 November 2020; Accepted: 22 July 2021;<br/>
        Published: 05 August 2021.<br/><br/>

        Edited by: Carlos Duarte, University of Lisbon, Portugal<br/>
        Reviewed by: Patricia Acosta-Vargas, University of the Americas, Ecuador Rosa Navarrete, Escuela Politécnica Nacional, Ecuador<br/><br/>
        
        Copyright © 2021 Yu, Shelton, Abou Nassif Mourad and Oulal. This is an open-access article distributed under the terms of the Creative Commons Attribution License (CC BY). <br/>The use, distribution or reproduction in other forums is permitted, provided the original author(s) and the copyright owner(s) are credited and that the original publication in this journal is cited, in accordance with accepted academic practice. No use, distribution or reproduction is permitted which does not comply with these terms.<br/>
      </p>
    </div>
  },
  {
    id: "SocialMusic",
    name: "Social Music",
    type: "Music Centered Social Media React Website",
    image: SocialMediaIcon,
    technology: "React, ExpressJS, Node.js, MySQL, Deezer Music API",
    card: {
      eyebrow: "Web app · Team project",
      pitch: "A social network for music fans. I led the team and built across the stack, then finished the features we didn't get to.",
      badge: "Team lead",
      badgeIcon: "team",
      tech: ["React", "Express", "Node.js", "MySQL"],
    },
    description: <p>Social Music is a social media platform allows users to keep track of music that they like and interact with other users. This project was originally a group project I created in college. I was the team lead on the project and worked on full stack development.<br /><br />
    I improved on the original project and implemented the unfinished and unpolished features.</p>,
    external:
    <div>
      <div className="videoWrapper pb-2">
        <YoutubeEmbed YoutubeID={"_oBbrOPj_4M"} title={"Social Music Demo"}/>
      </div>
      <div className="!w-full flex justify-center items-center flex-col">
        <Link href="https://github.com/ElAyachie/Social_Music" target="_blank" rel="noreferrer">
          <GitHubIcon className="!text-6xl !fill-[#DC4441] dark:!fill-[#3f51b5]"/>
        </Link>
      </div>
    </div>,
    copyright: 
    <div className="pl-6">
      <p>
        This project was originally created with the help of my partners Zachary Johnson, Fabio Marcellus, and Kyle McDevitt under the guidance of professor Dr. Charlie Pham At the Wentworth Institute of Technology.<br/><br/>
      </p>
    </div>
  }
];
